import { useEffect, useState } from "react";
import breakpoints from "./breakpoints.json";

/** The laptop tier's query (#1430, D383): the `lg` breakpoint, written once in breakpoints.json. */
export const LAPTOP_QUERY = `(min-width: ${breakpoints.lg}px)`;

/**
 * True from the laptop tier (1024px) up. For the one thing CSS cannot do: a screen that renders a second block
 * (an opened ground's card beside the list, the player page beside the squad) only when the two columns are on
 * screen. Layout itself is CSS (`.nw-split` in index.css); this only decides what is in the DOM. False where
 * `matchMedia` is missing, so a render with no browser asks for the phone's one column.
 */
export default function useLaptop() {
  return useMinWidth(LAPTOP_QUERY);
}

/** The tablet tier's query (#1481): the `md` breakpoint, from 768px. */
export const TABLET_QUERY = `(min-width: ${breakpoints.md}px)`;

/** True from the tablet tier (768px) up; same contract as `useLaptop`. */
export function useTablet() {
  return useMinWidth(TABLET_QUERY);
}

function useMinWidth(query) {
  const read = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/**
 * #1621 (D439): true below the tablet tier (under 768px) where `matchMedia` exists. A render with no media query
 * (a test, a server) is not a phone: it keeps the stacked page, as `useTablet` false would not.
 */
export function usePhone() {
  const tablet = useTablet();
  return !tablet && typeof window !== "undefined" && Boolean(window.matchMedia);
}
